import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card, Button, Form, Alert } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Quill from 'quill';
import 'quill-emoji/dist/quill-emoji.css';
import { EmojiBlot, ShortNameEmoji, ToolbarEmoji, TextAreaEmoji } from 'quill-emoji';
import { generateSlug } from '../../../utils/slugUtils';
import { convertCTToISO, convertISOToCTLocal, getDefaultScheduledTimeCT, ensureLinkDefaults } from './postFormHelpers';
import { quillModules, quillFormats, QuillStyles } from '../../ai-notes/quillConfig';
import { calculateSeoScore, getStatusIcon, getStatusColor } from '../../../utils/seoUtils';

if (typeof window !== 'undefined' && Quill && !(Quill.imports && Quill.imports['modules/emoji-toolbar'])) {
  Quill.register(
    {
      'formats/emoji': EmojiBlot,
      'modules/emoji-toolbar': ToolbarEmoji,
      'modules/emoji-textarea': TextAreaEmoji,
      'modules/emoji-shortname': ShortNameEmoji
    },
    true
  );
}

// Helper function to generate a clean excerpt for SEO description default
const generateDefaultDescription = (htmlContent, maxLength = 160) => {
  if (!htmlContent) return '';
  
  // Replace block-level tags and line breaks with spaces (to avoid smooshing words)
  let text = htmlContent
    .replace(/<br\s*\/?>/gi, ' ')           // <br> tags
    .replace(/<\/p>/gi, ' ')                 // closing </p>
    .replace(/<\/div>/gi, ' ')               // closing </div>
    .replace(/<\/li>/gi, ' ')                // closing </li>
    .replace(/<\/h[1-6]>/gi, ' ')            // closing headings
    .replace(/<\/blockquote>/gi, ' ')        // closing blockquote
    .replace(/<[^>]*>/g, '');                // remove remaining tags
  
  // Decode common HTML entities
  text = text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  
  // Normalize whitespace
  text = text.replace(/\s+/g, ' ').trim();
  
  // If already under limit, return as-is
  if (text.length <= maxLength) return text;
  
  // Find the last space before maxLength to avoid cutting words
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastSpace > maxLength * 0.7) {
    // Only use lastSpace if it's not too far back (at least 70% of max)
    return truncated.substring(0, lastSpace) + '...';
  }
  
  return truncated + '...';
};

const PostForm = ({ auth }) => {
  const history = useHistory();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState(null);
  const [scheduledForError, setScheduledForError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    status: 'published',
    tags: '',
    image_id: null,
    image_alt_text: '',
    slug: '',
    scheduled_for: '',
    meta_title: '',
    meta_description: ''
  });
  const [showSeoSection, setShowSeoSection] = useState(false);
  const [slugError, setSlugError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const quillRef = useRef(null);

  // Check admin authorization
  useEffect(() => {
    // auth === false means user is not authenticated (logged out)
    // auth === null/undefined means auth is still loading
    // Wait for auth to finish loading
    if (auth === null || auth === undefined) {
      return;
    }
    
    // If auth is false, user is logged out - redirect to homepage
    if (auth === false) {
      history.push('/');
      return;
    }
    
    // Now check if user is admin
    if (!auth.id || auth.role !== 'admin') {
      history.push('/');
    }
  }, [auth, history]);

  // Load post data if editing
  useEffect(() => {
    if (isEditMode && auth && auth.role === 'admin') {
      fetchPost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, auth]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/posts/admin/all`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const post = data.posts.find(p => p.id === parseInt(id));
        
        if (!post) {
          setError('Post not found');
          return;
        }

        // Format tags array to comma-separated string
        const tagsString = Array.isArray(post.tags) ? post.tags.join(', ') : '';
        
        // Generate default description from content
        const defaultDescription = generateDefaultDescription(post.content);

        setFormData({
          title: post.title || '',
          content: post.content || '',
          status: post.status || 'published',
          tags: tagsString,
          image_id: post.image_id || null,
          image_alt_text: post.image_alt_text || '',
          slug: post.slug || '',
          scheduled_for: post.scheduled_for ? convertISOToCTLocal(post.scheduled_for) : '',
          // Pre-populate SEO fields with saved values OR auto-generated defaults
          meta_title: post.meta_title || post.title || '',
          meta_description: post.meta_description || defaultDescription || ''
        });
        
        // Set image preview if image exists (from joined image association)
        if (post.image && (post.image.webp_url || post.image.s3_url)) {
          setImagePreview(post.image.webp_url || post.image.s3_url);
        }
      } else {
        setError('Failed to load post');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setScheduledForError(null);

    // Validation
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    // Check if content has actual text (not just HTML tags or empty)
    const textContent = formData.content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    if (!textContent) {
      setError('Content is required');
      return;
    }
    if (!formData.slug.trim()) {
      setError('URL slug is required');
      return;
    }
    
    // Validate slug format
    if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      setError('URL slug can only contain lowercase letters, numbers, and dashes');
      return;
    }
    
    // Validate scheduled_for if status is 'scheduled'
    if (formData.status === 'scheduled') {
      if (!formData.scheduled_for) {
        setScheduledForError('Scheduled date/time is required when status is Scheduled');
        return;
      }
      
      console.log('[Frontend Validation] scheduled_for value:', formData.scheduled_for);
      
      // Parse the datetime-local value (format: "YYYY-MM-DDTHH:mm")
      // We interpret this as CT time and compare to current CT time
      const [datePart, timePart] = formData.scheduled_for.split('T');
      if (!datePart || !timePart) {
        setScheduledForError('Invalid scheduled date/time format');
        return;
      }
      
      // Parse scheduled date/time components (as CT)
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes] = timePart.split(':').map(Number);
      
      console.log('[Frontend Validation] Parsed scheduled CT:', { year, month, day, hours, minutes });
      
      // Get current time in CT timezone
      const now = new Date();
      const nowCTStr = now.toLocaleString('en-US', { 
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      
      console.log('[Frontend Validation] Current CT time string:', nowCTStr);
      
      // Parse current CT time: format is "MM/DD/YYYY, HH:mm"
      const nowCTParts = nowCTStr.match(/(\d+)\/(\d+)\/(\d+), (\d+):(\d+)/);
      if (!nowCTParts) {
        setScheduledForError('Error getting current time');
        return;
      }
      
      // eslint-disable-next-line no-unused-vars
      const [_, nowMonth, nowDay, nowYear, nowHour, nowMinute] = nowCTParts.map(Number);
      
      console.log('[Frontend Validation] Parsed current CT:', { nowYear, nowMonth, nowDay, nowHour, nowMinute });
      
      // Compare date/time components directly (both in CT context)
      const scheduledDateTime = new Date(year, month - 1, day, hours, minutes, 0).getTime();
      const nowCTDateTime = new Date(nowYear, nowMonth - 1, nowDay, nowHour, nowMinute, 0).getTime();
      
      console.log('[Frontend Validation] Scheduled timestamp:', scheduledDateTime, 'Current timestamp:', nowCTDateTime);
      console.log('[Frontend Validation] Difference (ms):', scheduledDateTime - nowCTDateTime);
      console.log('[Frontend Validation] Is future?', scheduledDateTime > nowCTDateTime);
      
      // Scheduled time must be in the future
      if (scheduledDateTime <= nowCTDateTime) {
        console.log('[Frontend Validation] VALIDATION FAILED - scheduled time is not in the future');
        setScheduledForError('Scheduled date/time must be in the future');
        return;
      }
      
      console.log('[Frontend Validation] Validation passed');
    }
    
    // Check for duplicate slug
    const slugValid = await validateSlug(formData.slug, isEditMode ? id : null);
    if (!slugValid) {
      return;
    }

    setSubmitting(true);

    try {
      // Parse tags from comma-separated string to array
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      // Don't send posted_at - let the backend set it when publishing
      // This ensures the server (not browser) sets the timestamp
      
      // Ensure links have default attributes (target="_blank" rel="noopener noreferrer")
      const contentWithLinks = ensureLinkDefaults(formData.content);
      
      const payload = {
        title: formData.title.trim(),
        content: contentWithLinks, // Process links with default attributes
        status: formData.status,
        tags: tagsArray,
        image_id: formData.image_id || null,
        image_alt_text: formData.image_alt_text.trim() || null,
        slug: formData.slug.trim(),
        scheduled_for: formData.status === 'scheduled' && formData.scheduled_for
          ? (() => {
              const iso = convertCTToISO(formData.scheduled_for);
              console.log('[Submit] Converting scheduled_for:', formData.scheduled_for, '->', iso);
              return iso;
            })()
          : null,
        meta_title: formData.meta_title.trim() || null,
        meta_description: formData.meta_description.trim() || null
      };

      const url = isEditMode ? `/api/posts/${id}` : '/api/posts';
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        // Redirect to admin list
        history.push('/admin/ai-notes');
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error || 'Failed to save post';
        
        console.log('[Submit] Backend error:', errorMessage);
        
        // If error is about scheduled_for, show it under the field instead of at top
        const isScheduledError = errorMessage.toLowerCase().includes('scheduled_for') || 
                                 errorMessage.toLowerCase().includes('scheduled') ||
                                 errorMessage.toLowerCase().includes('future');
        
        if (isScheduledError) {
          console.log('[Submit] Routing to scheduledForError');
          setScheduledForError(errorMessage);
          setError(null); // Clear top error
        } else {
          console.log('[Submit] Routing to top error');
          setError(errorMessage);
          setScheduledForError(null); // Clear field error
        }
      }
    } catch (err) {
      console.log('[Submit] Exception:', err.message);
      // Check if it's a scheduled error in the exception message
      const errMessage = 'Error saving post: ' + err.message;
      if (err.message.toLowerCase().includes('scheduled') || err.message.toLowerCase().includes('future')) {
        setScheduledForError(errMessage);
        setError(null);
      } else {
        setError(errMessage);
        setScheduledForError(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Auto-generate slug from title if title changed and slug is empty or matches old title slug
      if (field === 'title') {
        const newSlug = generateSlug(value);
        // Only auto-update slug if it's empty or if it matches the slug generated from the previous title
        // This allows manual slug edits to persist
        if (!prev.slug || prev.slug === generateSlug(prev.title)) {
          newData.slug = newSlug;
        }
        // Auto-update meta_title if it's empty or matches the previous title
        if (!prev.meta_title || prev.meta_title === prev.title) {
          newData.meta_title = value;
        }
      }
      
      // Auto-update meta_description when content changes
      if (field === 'content') {
        const newDescription = generateDefaultDescription(value);
        // Auto-update meta_description if it's empty or matches the previous auto-generated description
        const prevDescription = generateDefaultDescription(prev.content);
        if (!prev.meta_description || prev.meta_description === prevDescription) {
          newData.meta_description = newDescription;
        }
      }
      
      return newData;
    });
    
    // Clear slug error when slug changes
    if (field === 'slug') {
      setSlugError(null);
    }
  };
  
  const validateSlug = async (slug, excludeId = null) => {
    if (!slug) {
      setSlugError('URL slug is required');
      return false;
    }
    
    // Check for duplicate slug
    try {
      const response = await fetch(`/api/posts/check-slug?slug=${encodeURIComponent(slug)}${excludeId ? `&excludeId=${excludeId}` : ''}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (!data.available) {
          setSlugError('This URL slug is already in use. Please choose a different one.');
          return false;
        }
      }
    } catch (err) {
      console.error('Error checking slug:', err);
      // Don't block submission if check fails, but warn user
    }
    
    setSlugError(null);
    return true;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    // Show immediate preview using FileReader (before upload completes)
    const reader = new FileReader();
    reader.onloadend = () => {
      console.log('FileReader loaded, setting preview:', reader.result.substring(0, 50) + '...');
      setImagePreview(reader.result);
    };
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      setError('Failed to read image file');
    };
    reader.readAsDataURL(file);

    setUploadingImage(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch('/api/posts/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Image uploaded successfully:', data);
        // Set the image_id from the created asset
        setFormData(prev => ({ ...prev, image_id: data.asset.id }));
        // Set preview to the WebP URL (or fallback to original)
        setImagePreview(data.asset.webp_url || data.asset.s3_url);
        console.log('Image preview set to:', data.asset.webp_url || data.asset.s3_url);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to upload image');
        // Clear preview if upload fails
        setImagePreview(null);
      }
    } catch (err) {
      setError('Error uploading image: ' + err.message);
      // Clear preview if upload fails
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image_id: null, image_alt_text: '' }));
    setImagePreview(null);
  };

  // Don't render if auth is still loading (null/undefined)
  // auth === false means logged out (redirect handled in useEffect)
  if (auth === null || auth === undefined) {
    return (
      <div style={{
        padding: '120px 60px 60px 60px',
        minHeight: 'calc(100vh - 200px)'
      }}>
        <Container>
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Checking permissions...</p>
          </div>
        </Container>
      </div>
    );
  }
  
  // If auth is false (logged out) or user is not admin, return null (redirect handled in useEffect)
  if (auth === false || !auth.id || auth.role !== 'admin') {
    return null;
  }

  if (loading) {
    return (
      <div style={{
        padding: '120px 60px 60px 60px',
        minHeight: 'calc(100vh - 200px)'
      }}>
        <Container>
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading post...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>{isEditMode ? 'Edit Post' : 'New Post'} - AI Notes Admin</title>
      </Helmet>
      <div style={{
        padding: '120px 60px 60px 60px',
        minHeight: 'calc(100vh - 200px)'
      }}>
        <Container fluid>
        <Row>
          <Col>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 style={{
                  fontWeight: '300',
                  color: '#2d3748',
                  marginBottom: '0.5rem'
                }}>
                  {isEditMode ? 'Edit Post' : 'Create New Post'}
                </h2>
                <p className="text-muted mb-0">
                  {isEditMode ? 'Update post details' : 'Create a new Post Notes post'}
                </p>
              </div>
              <Link to="/admin/ai-notes">
                <Button
                  variant="outline-secondary"
                  style={{
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  ← Back to List
                </Button>
              </Link>
            </div>

            {error && !scheduledForError && (
              <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
                {error}
              </Alert>
            )}

            {/* Form */}
            <Card style={{
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
            }}>
              <Card.Body style={{ padding: '2rem' }}>
                <Form onSubmit={handleSubmit}>
                  {/* Title */}
                  <Form.Group className="mb-4">
                    <Form.Label style={{
                      fontWeight: '500',
                      color: '#2d3748',
                      marginBottom: '8px'
                    }}>
                      Title <span style={{ color: '#e53e3e' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      placeholder="Enter post title..."
                      required
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        padding: '12px 16px',
                        fontSize: '1rem',
                        resize: 'vertical'
                      }}
                    />
                  </Form.Group>

                  {/* URL Slug */}
                  <Form.Group className="mb-4">
                    <Form.Label style={{
                      fontWeight: '500',
                      color: '#2d3748',
                      marginBottom: '8px'
                    }}>
                      URL Slug <span style={{ color: '#e53e3e' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.slug}
                      onChange={(e) => handleChange('slug', e.target.value)}
                      onBlur={() => validateSlug(formData.slug, isEditMode ? id : null)}
                      placeholder="post-url-slug"
                      required
                      style={{
                        borderRadius: '8px',
                        border: slugError ? '1px solid #e53e3e' : '1px solid #e2e8f0',
                        padding: '10px 16px',
                        fontSize: '1rem',
                        fontFamily: 'monospace'
                      }}
                    />
                    {slugError && (
                      <Form.Text style={{ color: '#e53e3e', fontSize: '0.875rem', display: 'block', marginTop: '4px' }}>
                        {slugError}
                      </Form.Text>
                    )}
                    <Form.Text className="text-muted" style={{ fontSize: '0.875rem' }}>
                      URL for this post: <code>/ai-notes/{formData.slug || 'post-url-slug'}</code>
                    </Form.Text>
                  </Form.Group>

                  {/* Image Upload */}
                  <Form.Group className="mb-4">
                    <Form.Label style={{
                      fontWeight: '500',
                      color: '#2d3748',
                      marginBottom: '8px'
                    }}>
                      Featured Image
                    </Form.Label>
                    {imagePreview ? (
                      <div className="mb-3">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '400px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            objectFit: 'contain',
                            display: 'block',
                            backgroundColor: '#f7fafc'
                          }}
                          onError={(e) => {
                            console.error('Image preview error:', imagePreview, e);
                            e.target.style.border = '2px solid #f56565';
                            e.target.alt = 'Image failed to load';
                          }}
                        />
                      </div>
                    ) : null}
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        padding: '10px 16px'
                      }}
                    />
                    {imagePreview && (
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                          <img
                            src={imagePreview}
                            alt="Preview"
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              border: '1px solid #e2e8f0'
                            }}
                          onError={(e) => {
                            console.error('Thumbnail image error:', imagePreview);
                            // Don't hide, just show error styling
                            e.target.style.border = '2px solid #f56565';
                          }}
                          />
                          <Form.Text className="text-muted" style={{ fontSize: '0.875rem', margin: 0 }}>
                            {imagePreview.split('/').pop()}
                          </Form.Text>
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={handleRemoveImage}
                          style={{
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            padding: '4px 12px'
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                    {uploadingImage && (
                      <Form.Text className="text-muted" style={{ fontSize: '0.875rem', display: 'block', marginTop: '4px' }}>
                        Uploading image...
                      </Form.Text>
                    )}
                    <Form.Text className="text-muted" style={{ fontSize: '0.875rem' }}>
                      Upload an image for this post (max 10MB). Supported formats: JPG, PNG, GIF, WebP
                    </Form.Text>
                    {/* Alt Text for Featured Image */}
                    {formData.image_id && (
                      <Form.Control
                        type="text"
                        placeholder="Image alt text (for SEO and accessibility)"
                        value={formData.image_alt_text}
                        onChange={(e) => handleChange('image_alt_text', e.target.value)}
                        style={{
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          padding: '10px 16px',
                          marginTop: '12px'
                        }}
                        maxLength={255}
                      />
                    )}
                  </Form.Group>

                  {/* Content - Rich Text Editor */}
                  <Form.Group className="mb-4">
                    <Form.Label style={{
                      fontWeight: '500',
                      color: '#2d3748',
                      marginBottom: '8px'
                    }}>
                      Content <span style={{ color: '#e53e3e' }}>*</span>
                    </Form.Label>
                    <div style={{
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={formData.content}
                        onChange={(value) => handleChange('content', value)}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Enter post content..."
                        style={{
                          minHeight: '400px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                    <Form.Text className="text-muted" style={{ fontSize: '0.875rem' }}>
                      Use the toolbar to format your content. Bold, italic, headers, lists, emojis, and links are supported. Links will open in a new tab by default.
                    </Form.Text>
                  </Form.Group>

                  {/* Status and Scheduled For */}
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label style={{
                          fontWeight: '500',
                          color: '#2d3748',
                          marginBottom: '8px'
                        }}>
                          Status
                        </Form.Label>
                        <Form.Select
                          value={formData.status}
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            handleChange('status', newStatus);
                            // Clear scheduled_for if status is not 'scheduled'
                            if (newStatus !== 'scheduled') {
                              handleChange('scheduled_for', '');
                            } else if (newStatus === 'scheduled' && !formData.scheduled_for) {
                              // If switching to scheduled and no date set, default to 2 hours from now with :00
                              const defaultTime = getDefaultScheduledTimeCT();
                              if (defaultTime) {
                                handleChange('scheduled_for', defaultTime);
                              }
                            }
                          }}
                          style={{
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            padding: '10px 16px'
                          }}
                        >
                          <option value="draft">Draft</option>
                          <option value="published">Published</option>
                          <option value="scheduled">Scheduled</option>
                        </Form.Select>
                        <Form.Text className="text-muted" style={{ fontSize: '0.875rem' }}>
                          {formData.status === 'draft' 
                            ? 'Drafts are not visible to the public'
                            : formData.status === 'scheduled'
                            ? 'Scheduled posts will be published automatically at the specified time'
                            : 'Published posts are visible to the public'}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label style={{
                          fontWeight: '500',
                          color: '#2d3748',
                          marginBottom: '8px'
                        }}>
                          {formData.status === 'scheduled' ? 'Schedule For (CT)' : 'Post at'}
                        </Form.Label>
                        {formData.status === 'scheduled' ? (
                          <>
                            <Form.Control
                              type="datetime-local"
                              value={formData.scheduled_for}
                              onChange={(e) => {
                                // Ensure minutes are always :00 (hourly scheduling only)
                                let value = e.target.value;
                                if (value) {
                                  // Replace minutes with :00
                                  value = value.replace(/:\d{2}$/, ':00');
                                }
                                handleChange('scheduled_for', value);
                                // Clear error when user changes the value
                                setScheduledForError(null);
                              }}
                              onFocus={(e) => {
                                // If empty, set to 2 hours from now with :00 (ensures it's always in the future)
                                if (!formData.scheduled_for) {
                                  const defaultTime = getDefaultScheduledTimeCT();
                                  if (defaultTime) {
                                    handleChange('scheduled_for', defaultTime);
                                  }
                                }
                                // Clear error when user focuses on the field
                                setScheduledForError(null);
                              }}
                              step="3600"
                              required={formData.status === 'scheduled'}
                              style={{
                                borderRadius: '8px',
                                border: scheduledForError ? '1px solid #e53e3e' : '1px solid #e2e8f0',
                                padding: '10px 16px',
                                fontSize: '1rem'
                              }}
                            />
                            {scheduledForError && (
                              <Form.Text style={{ color: '#e53e3e', fontSize: '0.875rem', display: 'block', marginTop: '4px' }}>
                                {scheduledForError}
                              </Form.Text>
                            )}
                            <Form.Text className="text-muted" style={{ fontSize: '0.875rem' }}>
                              Post will be published automatically at this date/time (Central Time, hourly only)
                            </Form.Text>
                          </>
                        ) : (
                          <>
                            <div style={{
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                              padding: '10px 16px',
                              backgroundColor: '#f7fafc',
                              color: '#4a5568',
                              fontSize: '1rem'
                            }}>
                              Immediately
                            </div>
                            <Form.Text className="text-muted" style={{ fontSize: '0.875rem' }}>
                              {formData.status === 'draft' 
                                ? 'Post will be published immediately when you change status to Published'
                                : 'Post will be published immediately when saved'}
                            </Form.Text>
                          </>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* SEO & Social Preview Section */}
                  <div className="mb-4">
                    {/* SEO Score Header */}
                    {(() => {
                      const seoData = calculateSeoScore({
                        title: formData.title,
                        meta_title: formData.meta_title,
                        meta_description: formData.meta_description,
                        content: formData.content,
                        image_id: formData.image_id,
                        image_alt_text: formData.image_alt_text,
                        image: imagePreview,
                        slug: formData.slug,
                        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : []
                      });
                      return (
                        <div
                          onClick={() => setShowSeoSection(!showSeoSection)}
                          style={{
                            cursor: 'pointer',
                            padding: '12px 16px',
                            backgroundColor: '#f7fafc',
                            borderRadius: showSeoSection ? '8px 8px 0 0' : '8px',
                            border: '1px solid #e2e8f0',
                            borderBottom: showSeoSection ? 'none' : '1px solid #e2e8f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontWeight: '500', color: '#2d3748' }}>
                              SEO Score & Preview
                            </span>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '4px 10px',
                              borderRadius: '16px',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              backgroundColor: seoData.gradeColor + '20',
                              color: seoData.gradeColor
                            }}>
                              {seoData.score}% - {seoData.grade}
                            </span>
                          </div>
                          <span style={{ color: '#718096' }}>
                            {showSeoSection ? '▼' : '▶'}
                          </span>
                        </div>
                      );
                    })()}
                    
                    {showSeoSection && (
                      <div style={{
                        padding: '1.5rem',
                        border: '1px solid #e2e8f0',
                        borderTop: 'none',
                        borderRadius: '0 0 8px 8px'
                      }}>
                        {/* SEO Checklist */}
                        {(() => {
                          const seoData = calculateSeoScore({
                            title: formData.title,
                            meta_title: formData.meta_title,
                            meta_description: formData.meta_description,
                            content: formData.content,
                            image_id: formData.image_id,
                            image_alt_text: formData.image_alt_text,
                            image: imagePreview,
                            slug: formData.slug,
                            tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : []
                          });
                          return (
                            <div style={{ marginBottom: '1.5rem' }}>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                gap: '8px'
                              }}>
                                {seoData.checks.map((check, index) => (
                                  <div 
                                    key={index}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '8px',
                                      padding: '8px 12px',
                                      backgroundColor: check.pass ? '#f0fff4' : '#fff5f5',
                                      borderRadius: '6px',
                                      border: `1px solid ${getStatusColor(check.status)}30`
                                    }}
                                  >
                                    <span style={{ 
                                      color: getStatusColor(check.status),
                                      fontWeight: '600',
                                      flexShrink: 0
                                    }}>
                                      {getStatusIcon(check.status)}
                                    </span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ 
                                        fontSize: '0.85rem', 
                                        fontWeight: '500',
                                        color: '#2d3748'
                                      }}>
                                        {check.name}
                                      </div>
                                      <div style={{ 
                                        fontSize: '0.75rem', 
                                        color: '#718096',
                                        marginTop: '2px'
                                      }}>
                                        {check.message}
                                      </div>
                                      {check.suggestion && (
                                        <div style={{ 
                                          fontSize: '0.7rem', 
                                          color: '#667eea',
                                          marginTop: '2px',
                                          fontStyle: 'italic'
                                        }}>
                                          💡 {check.suggestion}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div style={{
                                marginTop: '12px',
                                fontSize: '0.8rem',
                                color: '#718096',
                                textAlign: 'center'
                              }}>
                                {seoData.passedChecks}/{seoData.totalChecks} checks passed
                              </div>
                            </div>
                          );
                        })()}

                        {/* Meta Title */}
                        <Form.Group className="mb-4">
                          <Form.Label style={{ fontWeight: '500', color: '#2d3748', marginBottom: '8px' }}>
                            Page Title
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.meta_title}
                            onChange={(e) => handleChange('meta_title', e.target.value.slice(0, 70))}
                            placeholder="Enter custom title for search results..."
                            maxLength={70}
                            style={{
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                              padding: '10px 16px'
                            }}
                          />
                          <div className="d-flex justify-content-between mt-1">
                            <Form.Text className="text-muted" style={{ fontSize: '0.875rem' }}>
                              Custom title for search results & browser tab. Leave blank to use post title.
                            </Form.Text>
                            <Form.Text style={{ 
                              fontSize: '0.875rem',
                              color: formData.meta_title.length > 60 ? (formData.meta_title.length > 70 ? '#e53e3e' : '#d69e2e') : '#718096'
                            }}>
                              {formData.meta_title.length}/70
                            </Form.Text>
                          </div>
                        </Form.Group>

                        {/* Meta Description */}
                        <Form.Group className="mb-4">
                          <Form.Label style={{ fontWeight: '500', color: '#2d3748', marginBottom: '8px' }}>
                            Meta Description
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={formData.meta_description}
                            onChange={(e) => handleChange('meta_description', e.target.value.slice(0, 160))}
                            placeholder="Enter description for search results and social previews..."
                            maxLength={160}
                            style={{
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                              padding: '10px 16px',
                              resize: 'vertical'
                            }}
                          />
                          <div className="d-flex justify-content-between mt-1">
                            <Form.Text className="text-muted" style={{ fontSize: '0.875rem' }}>
                              Description for search results & social previews. Leave blank to auto-generate.
                            </Form.Text>
                            <Form.Text style={{ 
                              fontSize: '0.875rem',
                              color: formData.meta_description.length > 140 ? (formData.meta_description.length > 160 ? '#e53e3e' : '#d69e2e') : '#718096'
                            }}>
                              {formData.meta_description.length}/160
                            </Form.Text>
                          </div>
                        </Form.Group>

                        {/* Social Preview Card */}
                        <div>
                          <Form.Label style={{ fontWeight: '500', color: '#2d3748', marginBottom: '8px' }}>
                            📱 Social Preview
                          </Form.Label>
                          <div style={{
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: '#fff'
                          }}>
                            {/* Preview Image */}
                            <div style={{
                              height: '150px',
                              backgroundColor: '#f7fafc',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderBottom: '1px solid #e2e8f0'
                            }}>
                              {imagePreview ? (
                                <img 
                                  src={imagePreview} 
                                  alt="Preview" 
                                  style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'cover' 
                                  }} 
                                />
                              ) : (
                                <span style={{ color: '#a0aec0', fontSize: '0.875rem' }}>
                                  No image - upload a featured image above
                                </span>
                              )}
                            </div>
                            {/* Preview Content */}
                            <div style={{ padding: '12px' }}>
                              <div style={{ 
                                fontSize: '0.75rem', 
                                color: '#718096', 
                                marginBottom: '4px',
                                textTransform: 'uppercase'
                              }}>
                                elliotkoss.com
                              </div>
                              <div style={{ 
                                fontSize: '1rem', 
                                fontWeight: '600', 
                                color: '#1a202c',
                                marginBottom: '4px',
                                lineHeight: '1.3'
                              }}>
                                {formData.meta_title || formData.title || 'Post Title'}
                              </div>
                              <div style={{ 
                                fontSize: '0.875rem', 
                                color: '#4a5568',
                                lineHeight: '1.4',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}>
                                {formData.meta_description || generateDefaultDescription(formData.content) || 'Add content to see preview...'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Tags */}
                  <Form.Group className="mb-4">
                    <Form.Label style={{
                      fontWeight: '500',
                      color: '#2d3748',
                      marginBottom: '8px'
                    }}>
                      Tags (comma-separated)
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.tags}
                      onChange={(e) => handleChange('tags', e.target.value)}
                      placeholder="e.g., ai, machine learning, research"
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        padding: '10px 16px'
                      }}
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.875rem' }}>
                      Separate multiple tags with commas
                    </Form.Text>
                  </Form.Group>

                  {/* Action Buttons */}
                  <div className="d-flex gap-3 justify-content-end pt-3" style={{
                    borderTop: '1px solid #e2e8f0'
                  }}>
                    <Link to="/admin/ai-notes">
                      <Button
                        variant="outline-secondary"
                        type="button"
                        style={{
                          borderRadius: '8px',
                          padding: '10px 24px',
                          fontWeight: '500'
                        }}
                      >
                        Cancel
                      </Button>
                    </Link>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={submitting}
                      style={{
                        borderRadius: '8px',
                        padding: '10px 24px',
                        fontWeight: '500',
                        backgroundColor: '#667eea',
                        border: 'none'
                      }}
                    >
                      {submitting ? 'Saving...' : isEditMode ? 'Update Post' : 'Create Post'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      </div>
    </>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)((props) => (
  <>
    <QuillStyles />
    <PostForm {...props} />
  </>
));
